import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const useAnimateOnScroll = () => {
	const observerRef = useRef(null);
	const elementsRef = useRef(new Set());
	const location = useLocation();

	// Memoize the intersection handler
	const handleIntersection = useCallback((entries, observer) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				entry.target.classList.add('animate--in');
				observer.unobserve(entry.target);
				elementsRef.current.delete(entry.target);
			}
		});
	}, []);

	// Memoize setup function
	const setupAnimations = useCallback(() => {
		// Cleanup previous observations
		if (elementsRef.current.size > 0) {
			elementsRef.current.forEach((element) => {
				observerRef.current?.unobserve(element);
			});
			elementsRef.current.clear();
		}

		const elements = document.querySelectorAll('#main h1:not(.no-animate), ' + '#main h2:not(.no-animate), ' + '#main h3:not(.no-animate), ' + '#main h4:not(.no-animate), ' + '#main h5:not(.no-animate), ' + '#main h6:not(.no-animate), ' + '#main p:not(.no-animate), ' + '#main li:not(.no-animate)');

		elements.forEach((element) => {
			element.classList.remove('animate', 'animate--in');
			element.classList.add('animate');
			observerRef.current?.observe(element);
			elementsRef.current.add(element);
		});
	}, []);

	useEffect(() => {
		// Create observer with pixel-based trigger
		observerRef.current = new IntersectionObserver(handleIntersection, {
			threshold: 0,
			rootMargin: '-32px 0px 0px 0px',
		});

		const initialTimer = setTimeout(setupAnimations, 100);

		// Capture current values that will be needed for cleanup
		const currentElements = Array.from(elementsRef.current);
		const currentObserver = observerRef.current;

		return () => {
			clearTimeout(initialTimer);
			// Only use captured values in cleanup
			currentElements.forEach((element) => {
				currentObserver?.unobserve(element);
			});
			currentObserver?.disconnect();
		};
	}, [handleIntersection, setupAnimations]);

	// Reset animations when route changes
	useEffect(() => {
		setupAnimations();
	}, [location.pathname, setupAnimations]);

	return null;
};

export default useAnimateOnScroll;
